import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { SrOrderStatus } from '@plato/data/starrocks/types';
import type { EntryDateRange } from '@plato/ui-legacy/components/filters/types/filter-types';
import {
    DateRangeComparison,
    RelativeTimeUnit,
} from '@plato/ui-legacy/components/filters/types/filter-types';
import type { SetValue } from '@plato/ui-legacy/lib/hooks/use-query-params';
import {
    dateParam,
    endOfDayParam,
    enumParam,
    generateQueryParamsString,
    numberParam,
    objectParam,
    queryParamsConfig,
    requiredParam,
    stringArrayParam,
    stringParam,
    transientParam,
} from '@plato/ui-legacy/lib/query-params';
import { usePersistedQueryParams } from '../../../lib/hooks/use-persisted-query-params.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { QuotesOrdersSortBy, SortOrder } from '../../../server/types/list-view-types.ts';

export type QuoteOrderQueryFilters = Pick<
    RouterInput['platoUser']['getManyQuotesOrders']['getWithFilters'],
    | 'salesRepIds'
    | 'clerkIds'
    | 'storageLocationIds'
    | 'companyIds'
    | 'documentTypes'
    | 'searchText'
    | 'totalAmountNet'
    | 'totalMargin'
> & {
    // The schema's input type also admits ISO strings (for the AI-chat tool
    // layer); the URL parsers always produce Dates, so type against the output.
    entryDateRange?: EntryDateRange;
    orderStatus?: string[];
    sortingFilter: NonNullable<{
        field: QuotesOrdersSortBy;
        direction: SortOrder;
    }>;
};
const config = queryParamsConfig<QuoteOrderQueryFilters>({
    orderStatus: stringArrayParam(),
    salesRepIds: stringArrayParam(),
    clerkIds: stringArrayParam(),
    storageLocationIds: stringArrayParam(),
    companyIds: stringArrayParam(),
    documentTypes: stringArrayParam(),
    searchText: transientParam(stringParam()),
    sortingFilter: objectParam({
        field: requiredParam(enumParam(QuotesOrdersSortBy)),
        direction: requiredParam(enumParam(SortOrder)),
    }),
    entryDateRange: objectParam<NonNullable<EntryDateRange>>({
        from: dateParam(),
        to: endOfDayParam(),
        unit: enumParam(RelativeTimeUnit),
        count: numberParam(),
        comparison: enumParam(DateRangeComparison),
    }),
    // objectParam here returns a parser expecting required properties internally — cast to the
    // expected shape with optional min/max to satisfy the QueryParamsObjectConfig typing.
    totalAmountNet: objectParam<{ min?: number; max?: number }>({
        min: numberParam(),
        max: numberParam(),
    }),
    totalMargin: objectParam<{ min?: number; max?: number }>({
        min: numberParam(),
        max: numberParam(),
    }),
});

export const openOrdersFilter = {
    orderStatus: [SrOrderStatus.OPEN, SrOrderStatus.OPEN_IN_PICKING] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const openQuotesFilter = {
    orderStatus: [SrOrderStatus.OPEN] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const billedOrdersFilter = {
    orderStatus: [SrOrderStatus.BILLED] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const defaultQuoteOrderFilters = {
    salesRepIds: [] as string[],
    clerkIds: [] as string[],
    orderStatus: [] as string[],
    storageLocationIds: [] as string[],
    companyIds: [] as string[],
    documentTypes: [] as string[],
    searchText: '',
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    } as {
        field: QuotesOrdersSortBy;
        direction: SortOrder;
    },
} satisfies QuoteOrderQueryFilters;

export function generateSearchParamsForQuotesOrders(params: Partial<QuoteOrderQueryFilters>): string {
    return generateQueryParamsString(config, params);
}

export function useQuoteOrderListFilters(
    orderType: SrSalesOrderType,
): [QuoteOrderQueryFilters, SetValue<QuoteOrderQueryFilters>] {
    return usePersistedQueryParams(config, defaultQuoteOrderFilters, orderType);
}
